import { useEffect, useRef, useState } from "react";
import type { AuthUser } from "@zhuohui/contracts";
import { getCurrentUser, login, logout } from "./api/client";

export const loginBranding = {
  brand: "卓汇事业部",
  heading: "登录运营管理模拟器",
  subcopy: "多平台运营、巡店、客服绩效与质检管理",
} as const;

export const handoffEntry = "/handoff/outputs/index.html";

export const handoffViewIds = [
  "overviewView",
  "storedataView",
  "ksperformanceView",
  "pddperformanceView",
  "wxperformanceView",
  "dydataView",
  "wxstoredataView",
  "efficiencyView",
  "agentView",
  "weeklyView",
  "issuesView",
  "peopleView",
  "kpiView",
  "performanceView",
  "invoiceView",
  "fulfillmentView",
  "qualityView",
  "receptionView",
  "aftersalesView",
  "patrolView",
  "monthlyView",
  "standardsView",
  "productView",
  "tasksView",
  "reviewView",
  "flowView",
] as const;

const handoffViews = new Set(handoffViewIds.map((id) => id.slice(0, -4)));
const handoffFlagParameters = ["workflow", "time", "rules"] as const;

type HandoffLocation = Pick<Location, "hash" | "search">;
type OuterHandoffLocation = HandoffLocation & Pick<Location, "pathname">;
type FrameHandoffLocation = HandoffLocation & Pick<Location, "pathname">;
type HandoffFrameWindow = Window & { switchView?: (view: string) => void };

export type IframeHandoffSyncDecision =
  | { type: "none" }
  | { type: "navigate"; target: string }
  | { type: "switch-view"; view: string };

function handoffHashFromLocation(location: HandoffLocation): string {
  const view = location.hash.slice(1);
  return handoffViews.has(view) ? `#${view}` : "";
}

export function canonicalHandoffSuffix(location: HandoffLocation): string {
  const source = new URLSearchParams(location.search);
  const canonical = new URLSearchParams();
  const contexts = source.getAll("context");
  if (contexts.length === 1 && contexts[0] === "performance") {
    canonical.set("context", "performance");
  }
  for (const parameter of handoffFlagParameters) {
    const values = source.getAll(parameter);
    if (values.length === 1 && values[0] === "1") canonical.set(parameter, "1");
  }
  const search = canonical.toString();
  return `${search ? `?${search}` : ""}${handoffHashFromLocation(location)}`;
}

export function normalizedOuterHandoffPath(location: OuterHandoffLocation): string | null {
  const suffix = canonicalHandoffSuffix(location);
  return `${location.search}${location.hash}` === suffix ? null : `${location.pathname}${suffix}`;
}

export function iframeHandoffTarget(outerLocation: HandoffLocation, frameLocation: FrameHandoffLocation): string | null {
  const suffix = canonicalHandoffSuffix(outerLocation);
  return frameLocation.pathname === handoffEntry && `${frameLocation.search}${frameLocation.hash}` === suffix
    ? null
    : `${handoffEntry}${suffix}`;
}

export function iframeHandoffSyncDecision(
  outerLocation: HandoffLocation,
  frameLocation: FrameHandoffLocation,
  canSwitchView: boolean,
): IframeHandoffSyncDecision {
  const target = iframeHandoffTarget(outerLocation, frameLocation);
  if (!target) return { type: "none" };

  const canonicalSearch = new URL(target, "https://handoff.invalid").search;
  if (frameLocation.pathname !== handoffEntry || frameLocation.search !== canonicalSearch || !canSwitchView) {
    return { type: "navigate", target };
  }

  const canonicalHash = handoffHashFromLocation(outerLocation);
  if (!canonicalHash && frameLocation.hash === "#overview") return { type: "none" };
  return { type: "switch-view", view: canonicalHash.slice(1) || "overview" };
}

export function outerHandoffTarget(outerLocation: OuterHandoffLocation, frameLocation: FrameHandoffLocation): string | null {
  if (frameLocation.pathname !== handoffEntry) return null;
  const suffix = canonicalHandoffSuffix(frameLocation);
  return `${outerLocation.search}${outerLocation.hash}` === suffix ? null : `${outerLocation.pathname}${suffix}`;
}

export async function clearUserAfterSuccessfulLogout(
  logoutRequest: () => Promise<void>,
  clearUser: () => void,
) {
  await logoutRequest();
  clearUser();
}

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [logoutError, setLogoutError] = useState("");
  const [logoutPending, setLogoutPending] = useState(false);

  useEffect(() => {
    void getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  if (authLoading) return <div className="authScreen"><p>正在验证登录状态…</p></div>;
  if (!user) {
    return <LoginGate error={authError} onLogin={async (username, password) => {
      setAuthError("");
      try {
        setUser(await login(username, password));
      } catch (error) {
        setAuthError(error instanceof Error ? error.message : "登录失败");
      }
    }} />;
  }

  return <CanonicalWorkspace
    user={user}
    logoutError={logoutError}
    logoutPending={logoutPending}
    onLogout={async () => {
      setLogoutError("");
      setLogoutPending(true);
      try {
        await clearUserAfterSuccessfulLogout(logout, () => setUser(null));
      } catch (error) {
        setLogoutError(error instanceof Error ? error.message : "退出登录失败，请重试");
      } finally {
        setLogoutPending(false);
      }
    }}
  />;
}

function CanonicalWorkspace({
  user,
  logoutError,
  logoutPending,
  onLogout,
}: {
  user: AuthUser;
  logoutError: string;
  logoutPending: boolean;
  onLogout: () => Promise<void>;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pollRef = useRef<number | null>(null);
  const initialEntry = useRef(`${handoffEntry}${canonicalHandoffSuffix(window.location)}`);

  useEffect(() => {
    const syncFromOuterLocation = () => {
      const normalizedOuterPath = normalizedOuterHandoffPath(window.location);
      if (normalizedOuterPath) window.history.replaceState(null, "", normalizedOuterPath);
      try {
        const frameWindow = iframeRef.current?.contentWindow as HandoffFrameWindow | null;
        const frameLocation = frameWindow?.location;
        if (!frameWindow || !frameLocation) return;
        const decision = iframeHandoffSyncDecision(
          window.location,
          frameLocation,
          typeof frameWindow.switchView === "function",
        );
        if (decision.type === "navigate") frameLocation.replace(decision.target);
        if (decision.type === "switch-view") frameWindow.switchView?.(decision.view);
      } catch {}
    };
    syncFromOuterLocation();
    window.addEventListener("hashchange", syncFromOuterLocation);
    window.addEventListener("popstate", syncFromOuterLocation);
    return () => {
      window.removeEventListener("hashchange", syncFromOuterLocation);
      window.removeEventListener("popstate", syncFromOuterLocation);
    };
  }, []);

  useEffect(() => () => {
    if (pollRef.current !== null) window.clearInterval(pollRef.current);
  }, []);

  const syncLocationFromWorkspace = () => {
    try {
      const frameLocation = iframeRef.current?.contentWindow?.location;
      if (!frameLocation) return;
      const target = outerHandoffTarget(window.location, frameLocation);
      if (target) window.history.replaceState(null, "", target);
    } catch {}
  };

  const handleWorkspaceLoad = () => {
    if (pollRef.current !== null) window.clearInterval(pollRef.current);
    syncLocationFromWorkspace();
    pollRef.current = window.setInterval(syncLocationFromWorkspace, 250);
  };

  return (
    <main className="canonicalWorkspace">
      <iframe
        ref={iframeRef}
        className="canonicalWorkspaceFrame"
        src={initialEntry.current}
        title="卓汇事业部运营管理模拟器"
        onLoad={handleWorkspaceLoad}
      />
      {logoutError && <p className="workspaceSessionError" role="alert">{logoutError}</p>}
      <div className="workspaceSession" aria-label="登录会话">
        <button type="button" disabled={logoutPending} onClick={() => void onLogout()} title={`${user.displayName} · 退出登录`}>
          {logoutPending ? "退出中" : "退出"}
        </button>
      </div>
    </main>
  );
}

function LoginGate({ error, onLogin }: { error: string; onLogin: (username: string, password: string) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  return <main className="authScreen"><form className="authCard" onSubmit={(event) => {
    event.preventDefault();
    setSubmitting(true);
    void onLogin(username, password).finally(() => setSubmitting(false));
  }}>
    <span>{loginBranding.brand}</span>
    <h1>{loginBranding.heading}</h1>
    <p className="authSubcopy">{loginBranding.subcopy}</p>
    <label>用户名<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></label>
    <label>密码<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
    {error && <p className="authError">{error}</p>}
    <button disabled={submitting}>{submitting ? "登录中…" : "登录"}</button>
  </form></main>;
}
